/**
 * CategoryScroller — a Blinkit-style category tab strip.
 *
 * A single horizontally scrollable row of icon + label items on a soft cream
 * Rocket07 background. No cards, borders or shadows — just the icon, a small
 * centred label, and a golden underline under the active item. The row hides
 * its scrollbar and snaps as you swipe, stays compact (~76px tall), and it
 * auto-scrolls the active item into the centre — so landing on a page with a
 * category selected brings that tab into view.
 *
 * It is self-contained: pass your own `categories` / `activeId` / `onSelect`,
 * or drop it in with no props and it runs on the dummy data below, tracking the
 * active tab internally (tap to move the underline — exactly like the mock).
 */

import { useEffect, useRef, useState } from 'react';
import {
  LayoutGrid,
  Apple,
  Milk,
  Cookie,
  CupSoda,
  Croissant,
  Sparkles,
  House,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';

export interface Category {
  id: string;
  label: string;
  icon: LucideIcon;
}

/** Dummy Rocket07 categories — swap for live data via the `categories` prop. */
export const DEFAULT_CATEGORIES: Category[] = [
  { id: 'all', label: 'All', icon: LayoutGrid },
  { id: 'fruits', label: 'Fruits', icon: Apple },
  { id: 'dairy', label: 'Dairy', icon: Milk },
  { id: 'snacks', label: 'Snacks', icon: Cookie },
  { id: 'drinks', label: 'Drinks', icon: CupSoda },
  { id: 'bakery', label: 'Bakery', icon: Croissant },
  { id: 'care', label: 'Care', icon: Sparkles },
  { id: 'home', label: 'Home', icon: House },
];

export interface CategoryScrollerProps {
  /** Items to render. Defaults to {@link DEFAULT_CATEGORIES}. */
  categories?: Category[];
  /** Controlled active id. Omit to let the component track it internally. */
  activeId?: string;
  /** Fired with the selected id on every tap. */
  onSelect?: (id: string) => void;
  className?: string;
}

export function CategoryScroller({
  categories = DEFAULT_CATEGORIES,
  activeId,
  onSelect,
  className,
}: CategoryScrollerProps) {
  const [internalActive, setInternalActive] = useState<string | undefined>(categories[0]?.id);
  const active = activeId ?? internalActive;
  const listRef = useRef<HTMLUListElement>(null);

  const handleSelect = (id: string) => {
    if (activeId === undefined) setInternalActive(id);
    onSelect?.(id);
  };

  // Keep the active tab centred as the selection changes.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const activeEl = list.querySelector<HTMLElement>('[data-active="true"]');
    if (!activeEl) return;
    const target = activeEl.offsetLeft - (list.clientWidth - activeEl.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [active]);

  return (
    <nav aria-label="Product categories" className={cn('bg-cream', className)}>
      <ul
        ref={listRef}
        className="no-scrollbar relative mx-auto flex max-w-6xl snap-x snap-mandatory gap-1 overflow-x-auto scroll-smooth px-2 py-2.5"
      >
        {categories.map((category) => {
          const Icon = category.icon;
          const isActive = category.id === active;
          return (
            <li key={category.id} data-active={isActive || undefined} className="shrink-0 snap-start">
              <button
                type="button"
                onClick={() => handleSelect(category.id)}
                aria-current={isActive ? 'true' : undefined}
                className="flex w-[72px] flex-col items-center gap-1 pt-0.5 outline-none focus-visible:opacity-70"
              >
                <Icon
                  size={26}
                  strokeWidth={isActive ? 2.5 : 2}
                  className={cn(
                    'transition-transform duration-200 active:scale-90',
                    isActive ? 'text-ink' : 'text-ink/70',
                  )}
                />
                <span
                  className={cn(
                    'text-[12px] leading-none',
                    isActive ? 'font-bold text-ink' : 'font-medium text-ink/70',
                  )}
                >
                  {category.label}
                </span>
                <span
                  className={cn(
                    'mt-1 h-[3px] w-6 rounded-full transition-colors duration-200',
                    isActive ? 'bg-primary-600' : 'bg-transparent',
                  )}
                />
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default CategoryScroller;
